<script setup lang="ts">
import { computed, defineAsyncComponent } from 'vue'

import { breakpointsPrimeFlex, useBreakpoints } from '@vueuse/core'
import { useHead } from '@unhead/vue'
import { useDomPurify } from '../composables/domPurify'
import { useFathom } from '../composables/fathom'
import { useSentry } from '../composables/sentry'
import List from '../components/List.vue'
import DToggleableContent from '../components/DascoToggleableContent.vue'
import ImageProduct from '../components/ImageProduct.vue'
import ThumbnailPdfPreview from '../components/ThumbnailPdfPreview.vue'
import { facetsBaseModel } from '../constants/productFacetsBaseModel'
import FilestackService from '../services/FilestackService'
import { useBaseModelStore } from '../stores/BaseModelStore'
import { getTechSpecProps } from '../utils/typesenseGetTechSpecProps'

const { handleError } = useSentry()
const DascoResponsiveVideo = defineAsyncComponent(() => import('../components/DascoResponsiveVideo.vue'))

const store = useBaseModelStore()
const { replaceLineBreak } = useDomPurify()

const techSpecs = computed(() => {
  const specs: any = []
  try {
    const _specProps = getTechSpecProps(facetsBaseModel)
    const props = Object.keys(getTechSpecProps(_specProps))
    props.forEach((prop: string) => {
      // eslint-disable-next-line no-extra-boolean-cast
      if (!!store.model[prop]) {
        specs.push({
          key: prop,
          ..._specProps[prop],
          value: store.model[prop] || []
        })
      }
    })
    return specs.filter((spec: any) => spec.value.length)
  }
  catch (error: any) {
    handleError(error)
    return specs
  }
})

// Breakpoints for styling
const breakpoints: any = useBreakpoints(breakpointsPrimeFlex)
const smallerOrEqual = breakpoints.smallerOrEqual('sm')

function techSpecBorderRadius(idx: number) {
  if (idx === 0)
    return smallerOrEqual ? 'border-top-left-radius: var(--border-radius); border-top-right-radius: var(--border-radius);' : 'border-top-left-radius: var(--border-radius);'
  if (idx === techSpecs.value.length - 1)
    return smallerOrEqual ? '' : 'border-bottom-left-radius: var(--border-radius)'
  return ''
}

const { fathom, FathomEvents } = useFathom()
function trackDatasheetEvent() {
  fathom.trackEvent(FathomEvents.DownloadDatasheet([store.model.vendor, store.model.model]))
}

interface TrackViewRelatedModel {
  vendor: string | null
  model: string | null
}
function trackViewRelatedModel({ vendor, model }: TrackViewRelatedModel) {
  fathom.trackEvent(FathomEvents.ViewRelatedModel(['from', store.model.vendor, store.model.model, 'to', vendor, model]))
}

// HEADER
useHead({
  title: store.model ? `${store.model.vendor} ${store.model.model} Base Model` : 'Base Model',
  meta: [
    {
      name: 'description',
      content: () => `${store.model?.overview?.length ? `${store.model.overview.substring(0, 55)}...` : `Product info for ${store.model.vendor} ${store.model.model}`}`
    }
  ]
})
</script>

<template>
  <section class="bg-white border-round p-1 lg:p-4">
    <div class="grid">
      <div class="col-12 lg:col-4">
        <image-product
          :key="`image-product-${store.model.model_uri}`"
          :model="store.model.model"
          :handle="store.model?.cdn_handle_primary_image || null"
        />
        <a
          class="p-button text-decoration-none block p-button-outlined border-gray-200 uppercase text-red-500 hover:bg-blue-50 hover:border-gray-400 text-2xl"
          :disabled="!store.model.cdn_handle_datasheet"
          :href="FilestackService.publicReadUrl(store.model.cdn_handle_datasheet)"
          target="_blank"
          @click="trackDatasheetEvent"
        >
          <fa-icon
            icon="fa fa-file-pdf"
            class="mr-2 text-red-600"
          />
          Datasheet
        </a>
        <a
          v-if="store.productInquiryUrl"
          class="mt-4 p-button text-decoration-none block p-button-outlined border-red-500 uppercase text-white bg-red-500 hover:bg-red-600 hover:border-red-600 text-2xl"
          :disabled="!store.model.cdn_handle_datasheet"
          :href="store.productInquiryUrl"
          target="_blank"
        >
          <fa-icon
            icon="fa-light fa-file-invoice"
            class="mr-2 text-red-900"
          />
          Submit Inquiry
        </a>
      </div>
      <div class="col-12 md:col-6 lg:col-8">
        <div class="text-red-500 text-4xl">
          <span class="text-gray-500 mr-1">{{ store.model.vendor }}</span>
          {{ store.model.model }}
        </div>
        <div class="text-sm">
          <span class="text-gray-500 font-bold mr-1">{{ store.model.category }}</span>
          <fa-icon
            icon="fa-solid fa-caret-right"
            class="fa-fw text-400 mr-1"
          />
          <span class="text-600">{{ store.model.category_2 }}</span>
        </div>
        <p
          v-if="replaceLineBreak(store.model.overview)"
          v-html="replaceLineBreak(store.model.overview)"
        />
        <div class="grid m-0">
          <div
            v-if="store.model?.applications?.length"
            class="col-12 lg:col-6"
          >
            <h2 class="text-xl text-red-500 uppercase">
              Applications
            </h2>
            <div
              v-for="a in store.model.applications"
              :key="a.label"
            >
              <list
                :label="a.label"
                :items="a.items"
              />
            </div>
          </div>
          <div
            v-if="store.model?.features?.length"
            class="col-12 lg:col-6"
          >
            <h2 class="text-xl text-red-500 uppercase">
              Key Features
            </h2>
            <list :items="store.model.features" />
          </div>
        </div>
      </div>
    </div>
  </section>
  <section class="bg-white border-round p-1 lg:p-4 mt-4">
    <div class="grid">
      <div class="col-12 lg:col-6">
        <h2 class="text-xl text-red-500 uppercase">
          Specs &amp; Resources
        </h2>
        <d-toggleable-content
          :key="`tech-specs-${store.model.model}`"
          title="Tech Specs"
          :start-open="true"
        >
          <div class="border-1 border-gray-300 border-round">
            <div
              v-for="(spec, idx) in techSpecs"
              :key="spec.key"
              class="grid m-0 border-gray-300 text-sm"
              :class="idx === techSpecs.length - 1 ? '' : 'border-bottom-1 '"
            >
              <div
                class="col-12 md:col-6 surface-100 font-medium"
                :style="techSpecBorderRadius(idx)"
              >
                {{ spec.label }}
              </div>
              <div class="col-12 md:col-6">
                {{ spec.value.join(', ') }}
              </div>
            </div>
          </div>
        </d-toggleable-content>
        <!-- TODO: Hide if none -->
        <d-toggleable-content
          v-if="store.model?.additional_docs?.length"
          :key="`additional-resources-${store.model.model}`"
          class="mt-3 border-top-1 border-gray-300 pt-3"
          title="Additional Resources"
        >
          <div class="grid m-0">
            <div
              v-for="doc in store.model.additional_docs"
              :key="doc.cdn_key"
              class="col-6 md:col-4 lg:col-3"
            >
              <thumbnail-pdf-preview
                :cdn-key="doc.cdn_key"
                :title="doc.title || null"
                :category="doc.category || null"
              />
            </div>
          </div>
        </d-toggleable-content>
        <d-toggleable-content
          v-if="store.model.resources_videos.length || store.model.resources_burping_video"
          :key="`video-resources-${store.model.model}`"
          class="mt-3 border-top-1 border-gray-300 pt-3"
          title="Video Resources"
        >
          <!-- TODO handle other video resources -->
          <div v-if="store.model.resources_burping_video">
            <div class="text-red-500 uppercase mb-1 text-xl">
              How to burp or vent a Pressure Gauge
            </div>
            <dasco-responsive-video
              class="border-round"
              src="dVmWk4b-vY4"
              service="youtube"
              :width="1280"
              :height="720"
            />
          </div>
        </d-toggleable-content>
      </div>
      <div
        v-if="store.model?.related_models?.length"
        class="col-12 lg:col-6"
      >
        <!-- TODO: Hide if none -->
        <h2 class="text-xl text-red-500">
          Related Models
        </h2>
        <div class="grid">
          <div
            v-for="i in store.model.related_models"
            :key="i.id"
            class="col-12 lg:col-6"
          >
            <div class="p-4 border-2 border-transparent hover:border-red-500 border-round">
              <router-link
                class="router-link"
                :to="`/models/${i.model_uri}`"
                @click="trackViewRelatedModel({ vendor: i.vendor, model: i.model })"
              >
                <div class="text-red-500 text-2xl lg:text-3xl">
                  <span class="text-gray-500 mr-1">{{ i.vendor }}</span>
                  {{ i.model }}
                </div>
                <div class="text-sm">
                  <span class="text-600 font-bold mr-1">{{ i.category }}</span>
                  <template v-if="i?.category_2?.length || i?.category_detail?.length">
                    <fa-icon
                      icon="fa-solid fa-caret-right"
                      class="fa-fw text-400 mr-1"
                    />
                    <span class="text-600">{{ i.category_2 || i.category_detail }}</span>
                  </template>
                </div>
                <image-product
                  v-if="i?.cdn_handle_primary_image"
                  :key="`image-product-${i.model_uri}`"
                  :handle="i?.cdn_handle_primary_image || null"
                  :model="i.model"
                />
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
